import { Icon, type IconProps, } from "@bracket/ui/components";
import { Panel, TextNode, } from "@bracket/ui/nodes";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode, type SyntheticEvent, } from "react";
import styled from "styled-components";
import { getTheme, themed, } from "@bracket/ui/themes";
import type { CssSpaceValue, Strata, ThemePalette, ColorDeclaration, } from "@bracket/ui/types";



export declare type IconButtonProps = Omit<IconProps, "size" | "scale" | "rotation" | "circle" | "circlePad" | "circleWidth" | "circleColor"> & {
	onClick?: (evt: SyntheticEvent<HTMLDivElement>) => void;
	disabled?: boolean;
	className?: string;
	type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
	"aria-label"?: string;
	"aria-labelledby"?: string;
	/** Visible label rendered next to the icon */
	label?: string | ReactNode;
	/** Icon position relative to label - "start" (left) or "end" (right) */
	iconAlign?: "start" | "end";
	button?: boolean;
	color?: ColorDeclaration | keyof ThemePalette;
	iconSize?: string;
	iconScale?: string | number;
	iconRotation?: number;
	iconCircle?: boolean;
	iconCirclePad?: string;
	iconCircleWidth?: string;
	iconCircleColor?: string;
	pad?: CssSpaceValue
	margin?: CssSpaceValue
	/** explicit icon color to pass through to Icon */
	iconColor?: keyof ThemePalette | ColorDeclaration;
	/** strata level to pass through to the IconButton */
	strata?: Strata;
	/** whether to wrap the label and icon */
	nowrap?: boolean;
};







/**
 * An icon-only button component that supports any icon from the Icons library.
 *
 * @component
 * @param {Object} props - Component props
 * @param {keyof typeof ICONS} props.icon - Icon name from ICONS to display
 * @param {() => void} [props.onClick] - Click handler function
 * @param {boolean} [props.disabled=false] - Whether button is disabled
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {"button"|"submit"|"reset"} [props.type="button"] - HTML button type
 * @param {string} [props.aria-label] - Accessible label for the button
 * @param {string} [props.aria-labelledby] - ID of element that labels the button
 * @param {string} [props.iconSize] - Size of the icon (passed through to Icon component as size)
 * @param {string | number} [props.iconScale] - CSS scale value passed through to the Icon component as scale (e.g., "1.5" or 1.5)
 * @param {number} [props.iconRotation] - Rotation angle in degrees (1-360, passed through to Icon component as rotation)
 * @param {boolean} [props.iconCircle] - Whether to draw a circle border around the icon (passed through to Icon component as circle)
 * @param {string} [props.iconCirclePad] - CSS size value for padding between circle border and icon (passed through to Icon component as circlePad)
 * @param {string} [props.iconCircleWidth] - CSS size value for circle border width (passed through to Icon component as circleWidth, default: "1px")
 * @param {string} [props.iconCircleColor] - CSS color value for circle border (passed through to Icon component as circleColor, default: "#fff")
 * @returns {JSX.Element} An icon button component
 */
export const IconButton = forwardRef<HTMLDivElement, IconButtonProps>(({ 
	icon,
	onClick = () => {},
	disabled = false,
	className = "",
	button = true,
	label,
	iconAlign = "start",
	iconSize,
	iconScale,
	iconRotation,
	iconCircle,
	iconCirclePad,
	iconCircleWidth,
	iconCircleColor,
	iconColor,
	color,
	pad,
	margin,
	strata = "default",
	nowrap = false,
	...props
}, ref) => {
	themed();

	const theme = getTheme();
	color = iconColor ? theme[iconColor as keyof ThemePalette] : iconColor ?? theme.surface_on;

	const iconElement = ( icon &&
		<Icon
			icon={icon}
			size={iconSize}
			scale={iconScale}
			rotation={iconRotation}
			color={color}
			circle={iconCircle}
			circlePad={iconCirclePad}
			circleWidth={iconCircleWidth}
			circleColor={iconCircleColor} />
	);
	const labelElement = label ? <TextNode $variant="p" $nowrap={nowrap}>{label}</TextNode> : null;

	return (
		<StyledButton ref={ref}
			$visible={button}
			onClick={!disabled ? onClick : undefined}
			$disabled={disabled}
			$strata={strata}
			$pad={pad}
			$margin={margin}
			className={className}
			{...props}
		>
			{iconAlign === "start" ? (
				<>
					{iconElement}
					{labelElement}
				</>
			) : (
				<>
					{labelElement}
					{iconElement}
				</>
			)}
		</StyledButton>
	);
});


const StyledButton = styled(Panel)<{ $disabled?: boolean; $visible?: boolean; $strata?: Strata; $pad?: CssSpaceValue; $margin?: CssSpaceValue;}>`

    justify-content: center;
    align-items: center;
    border: none;
		flex-flow: row;
    border-radius: 8px;
    padding: 0.5rem;
    background: ${({ $visible, }) => $visible ? "transparent" : "transparent"};
		transition: opacity 0.2s ease;
    gap: 0.5rem;
		${({ $pad }) => $pad !== undefined ? `padding: ${$pad};` : ""}
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
		&:hover {
			opacity: 0.7;
    }
`;
